import { WIKI_CONFIG } from '../config';

const TOKEN_KEY = 'wiki_admin_token';
const EXPIRY_KEY = 'wiki_admin_token_expiry';
const USERNAME_KEY = 'wiki_admin_username';
const NICKNAME_KEY = 'wiki_admin_nickname';
const ID_KEY = 'wiki_admin_id';

/**
 * Декодирует payload JWT токена без верификации подписи (только для UI).
 */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * Возвращает список прав из текущего JWT токена.
 * Права — это строки из claim "permissions" (например, ["MANAGE_MAP", "MANAGE_PAGES"]).
 */
export function getWikiPermissions() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  if (!payload) return [];
  return Array.isArray(payload.permissions) ? payload.permissions : [];
}

/**
 * Возвращает роль из текущего JWT токена (ROLE_ADMIN / ROLE_EDITOR).
 */
export function getWikiRole() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return payload?.role ?? null;
}

/**
 * Проверяет, есть ли у текущего пользователя указанное право.
 * ROLE_ADMIN имеет все права автоматически.
 */
export function hasWikiPermission(permission) {
  if (getWikiRole() === 'ROLE_ADMIN') return true;
  return getWikiPermissions().includes(permission);
}

/**
 * Логин через API: POST /api/auth/login, тело { username, password }.
 * Ответ 200: { token, expiresIn, username, role, message }. Сохраняем JWT и срок.
 */
export async function wikiLogin(username, password) {
  const url = `${WIKI_CONFIG.apiUrl}/api/auth/login`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch (e) {
    return { success: false, message: 'Сервер не ответил. Проверьте, что бэкенд запущен и доступен.' };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { success: false, message: data?.message || 'Неверный логин или пароль' };
  }
  const token = data.token;
  const expiresIn = data.expiresIn != null ? Number(data.expiresIn) : 3600000; // backend returns milliseconds
  const expiry = decodeJwtPayload(token)?.exp * 1000 || Date.now() + expiresIn;
  const name = data.username || username;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EXPIRY_KEY, String(expiry));
  localStorage.setItem(USERNAME_KEY, name);
  localStorage.setItem(NICKNAME_KEY, name);
  localStorage.setItem(ID_KEY, data.id != null ? String(data.id) : '1');
  return { success: true, admin: { id: data.id, username: name, nickname: name } };
}

export function wikiLogout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  localStorage.removeItem(USERNAME_KEY);
  localStorage.removeItem(NICKNAME_KEY);
  localStorage.removeItem(ID_KEY);
}

export function isWikiAuthenticated() {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(EXPIRY_KEY);
  if (!token || !expiry) return false;
  if (Date.now() > Math.min(Number(expiry), decodeJwtPayload(token)?.exp * 1000 || Number(expiry))) {
    wikiLogout();
    return false;
  }
  return true;
}

export function getWikiUsername() {
  return localStorage.getItem(USERNAME_KEY) || '';
}

export function getWikiNickname() {
  return localStorage.getItem(NICKNAME_KEY) || getWikiUsername();
}

export function getWikiAdminId() {
  return localStorage.getItem(ID_KEY) || '';
}

/** Проверяет, является ли текущий пользователь администратором (ROLE_ADMIN). */
export function isWikiAdmin() {
  return getWikiRole() === 'ROLE_ADMIN';
}
